import React, { useEffect, useState, useCallback } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Loader2, RefreshCw, Landmark, Link2, Copy, Banknote } from 'lucide-react';
import RelmBankingRail from './RelmBankingRail';

const RelmLayer = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ payer_name: '', payer_address: '', payer_email: '', amount: '', description: 'Payment to Bullaale AS' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/relm/overview');
      setData(r.data);
    } catch (e) {
      toast.error('Failed to load RELM overview');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const createInvoice = async () => {
    if (!form.payer_name) { toast.error('Payer name is required'); return; }
    setCreating(true);
    try {
      const r = await api.post('/relm/invoice', {
        payer_name: form.payer_name,
        payer_address: form.payer_address,
        payer_email: form.payer_email,
        amount: form.amount ? parseFloat(form.amount) : 0,
        currency: 'USD',
        description: form.description,
      });
      toast.success(`Invoice ${r.data.invoice.invoice_no} created`);
      navigator.clipboard?.writeText(r.data.public_url).catch(() => {});
      setForm({ payer_name: '', payer_address: '', payer_email: '', amount: '', description: 'Payment to Bullaale AS' });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Invoice creation failed');
    } finally {
      setCreating(false);
    }
  };

  const copyLink = (id) => {
    const url = `${window.location.origin}/pay/relm/${id}`;
    navigator.clipboard.writeText(url);
    toast.success('Payment link copied');
  };

  const acct = data?.account || {};
  const banking = data?.banking || {};

  return (
    <div className="space-y-6" data-testid="relm-layer">
      <Card className="p-6 border-cyan-200" data-testid="relm-overview-card">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-3">
            <Landmark className="w-5 h-5 text-cyan-600" />
            <h3 className="text-lg font-semibold">RELM — USD Account (Bullaale AS)</h3>
          </div>
          <div className="flex items-center gap-2">
            {acct.api_configured
              ? <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid="relm-api-badge">BANKING API CONNECTED (LIVE)</Badge>
              : <Badge className="bg-amber-50 text-amber-800 border border-amber-200" data-testid="relm-api-badge">API NOT CONFIGURED</Badge>}
            <Badge className="bg-slate-100 text-slate-600 border border-slate-200" data-testid="relm-mode-badge">BANKING-ONLY MODE</Badge>
            <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="relm-refresh-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            </Button>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div><p className="text-xs text-slate-400">Account holder</p><p className="font-medium" data-testid="relm-account-name">{acct.account_name || '—'}</p></div>
          <div><p className="text-xs text-slate-400">Account number</p><p className="font-mono" data-testid="relm-account-number">{acct.account_number || '—'}</p></div>
          <div><p className="text-xs text-slate-400">Wire routing (ABA)</p><p className="font-mono" data-testid="relm-routing">{acct.routing_number || '—'}</p></div>
          <div><p className="text-xs text-slate-400">Bank</p><p data-testid="relm-bank">{acct.bank || '—'}</p></div>
        </div>
      </Card>

      <Card className="p-6 border-emerald-200" data-testid="relm-banking-card">
        <div className="flex items-center gap-3 mb-4">
          <Banknote className="w-5 h-5 text-emerald-600" />
          <h3 className="text-lg font-semibold">Live Balances (Banking API)</h3>
          {banking.checked_at && <span className="text-[11px] text-slate-400" data-testid="relm-banking-checked-at">checked {banking.checked_at}</span>}
        </div>
        {banking.available ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            {(banking.balances || []).map((b) => (
              <div key={b.currency} data-testid={`relm-balance-${b.currency}`}>
                <p className="text-xs text-slate-400">{b.currency}</p>
                <p className="font-mono text-lg">{b.balance}</p>
                <p className="text-[11px] text-slate-400">available {b.available}</p>
              </div>
            ))}
            <div data-testid="relm-txn-count">
              <p className="text-xs text-slate-400">Ledger transactions</p>
              <p className="font-mono text-lg">{banking.transaction_count ?? '—'}</p>
            </div>
          </div>
        ) : (
          <p className="text-sm text-slate-500" data-testid="relm-banking-unavailable">Banking API unavailable{banking.error ? ` — ${banking.error}` : ''}</p>
        )}
      </Card>

      <Card className="p-6" data-testid="relm-invoice-card">
        <div className="flex items-center gap-3 mb-4">
          <Link2 className="w-5 h-5 text-cyan-600" />
          <h3 className="text-lg font-semibold">Create Payment Link (USD bank transfer)</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><Label>Payer name *</Label>
            <Input value={form.payer_name} onChange={(e) => setForm({ ...form, payer_name: e.target.value })} placeholder="Payer full name" data-testid="relm-payer-name-input" /></div>
          <div><Label>Payer email</Label>
            <Input value={form.payer_email} onChange={(e) => setForm({ ...form, payer_email: e.target.value })} placeholder="payer@email.com" data-testid="relm-payer-email-input" /></div>
          <div className="md:col-span-2"><Label>Payer address</Label>
            <Input value={form.payer_address} onChange={(e) => setForm({ ...form, payer_address: e.target.value })} placeholder="Street, city, country" data-testid="relm-payer-address-input" /></div>
          <div><Label>Amount USD (blank = open amount)</Label>
            <Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="Open — payer enters amount" data-testid="relm-amount-input" /></div>
          <div><Label>Description</Label>
            <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="relm-description-input" /></div>
        </div>
        <Button className="mt-4 bg-cyan-600 hover:bg-cyan-700" onClick={createInvoice} disabled={creating} data-testid="relm-create-invoice-btn">
          {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Link2 className="w-4 h-4 mr-2" />}
          Create payment link
        </Button>
        <p className="text-[11px] text-slate-400 mt-2">Payment links show USD bank-transfer instructions for the named account (banking-only integration).</p>
      </Card>

      <Card className="p-6" data-testid="relm-invoices-table-card">
        <h3 className="text-lg font-semibold mb-3">Invoices</h3>
        {(data?.invoices || []).length === 0 ? (
          <p className="text-sm text-slate-500" data-testid="relm-invoices-empty">No invoices yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="relm-invoices-table">
              <thead className="text-[10px] uppercase text-slate-500 bg-slate-50">
                <tr>
                  <th className="text-left px-2 py-1.5">Invoice</th>
                  <th className="text-left px-2 py-1.5">Payer</th>
                  <th className="text-right px-2 py-1.5">Amount</th>
                  <th className="text-left px-2 py-1.5">Status</th>
                  <th className="text-left px-2 py-1.5">Created</th>
                  <th className="text-left px-2 py-1.5">Link</th>
                </tr>
              </thead>
              <tbody>
                {data.invoices.map((inv) => (
                  <tr key={inv.id} className="border-t border-slate-100" data-testid={`relm-invoice-row-${inv.invoice_no}`}>
                    <td className="px-2 py-1.5 font-mono">{inv.invoice_no}</td>
                    <td className="px-2 py-1.5">{inv.payer_name}</td>
                    <td className="px-2 py-1.5 text-right font-mono">{inv.open_amount ? 'OPEN' : `$ ${Number(inv.amount).toFixed(2)}`}</td>
                    <td className="px-2 py-1.5"><Badge variant="outline" className="text-[10px]">{inv.status}</Badge></td>
                    <td className="px-2 py-1.5 font-mono text-[10px]">{(inv.created_at || '').slice(0, 10)}</td>
                    <td className="px-2 py-1.5">
                      <Button variant="ghost" size="sm" onClick={() => copyLink(inv.id)} data-testid={`relm-copy-link-${inv.invoice_no}`}>
                        <Copy className="w-3 h-3" />
                      </Button>
                      <a href={`${process.env.REACT_APP_BACKEND_URL}/api/relm/invoice/${inv.id}/pdf`} className="text-[10px] text-cyan-700 hover:underline ml-1" data-testid={`relm-pdf-link-${inv.invoice_no}`}>PDF</a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <RelmBankingRail />
    </div>
  );
};

export default RelmLayer;
